



































































































import { ObjectType, Exprs, Types } from 'expangine-runtime';
import { friendlyList } from '@/common';
import { getConfirmation } from '@/app/Confirm';
import { getInput } from '@/app/Input';
import { sendNotification } from '@/app/Notify';
import { getBuildType } from '@/app/BuildType';
import { getProgram } from '@/app/GetProgram';
import { Preferences, PreferenceCategory } from '@/app/Preference';
import { TypeUpdateEvent } from '../TypeVisuals';
import TypeEditorBase from '../TypeEditorBase';


const PREF_REMOVE_PROP = Preferences.define({
  key: 'object_remove_prop',
  label: 'Remove Object type property without confirmation',
  category: [PreferenceCategory.DESIGN, PreferenceCategory.CONFIRM],
  defaultValue: false,
});


export default TypeEditorBase<ObjectType, any, string>().extend({
  name: 'ObjectEditor',
  data: () => ({
    addProp: '',
    propChange: 1,
  }),
  computed: {
    isValidProp(): boolean {
      return !!(this.addProp && !(this.addProp in this.type.options.props) && this.registry.isValidProperty(this.addProp, this.type));
    },
    description(): string {
      return friendlyList(Object.keys(this.type.options.props));
    },
  },
  methods: {
    requiredTypeFor(prop: string) {
      if (!(this.requiredType instanceof ObjectType)) {
        return null;
      }

      return this.requiredType.options.props[prop] 
        || this.requiredType.getWildcardType();
    },
    async add() {
      const chosen = await getBuildType({
        title: 'Add Property',
        input: {
          registry: this.registry,
          parent: this.type,
          parentSettings: this.settings,
        },
      });

      const propName = this.addProp;

      if (!propName || !chosen) {
        return;
      }

      this.addProp = '';

      const changeEvent: Partial<TypeUpdateEvent> = {};

      if (!this.noTransform) {
        const result = await getProgram({
          title: `Add property "${propName}"`,
          message: 'The default value for the new property.',
          confirm: 'Add',
          registry: this.registry,
          context: Types.object({
            parent: this.type,
          }),
          program: chosen.transform
            ? chosen.transform
            : this.registry.getTypeCreate(chosen.type),
          expectedType: chosen.type,
        });

        if (!result) {
          return sendNotification({ message: 'Property add canceled.' });
        }

        changeEvent.transform = this.type.getValueChangeExpression(result.program, undefined, propName);
      }
      
      this.$set(this.type.options.props, propName, chosen.type);
      this.$set(this.settings.sub, propName, chosen.settings);

      this.inputSelected.onSubAdd(propName, this.type, this.settings);

      this.change(changeEvent);
      this.propChange++;

      this.$emit('prop:add', propName);
    },
    async remove(prop: string) {
      if (!await getConfirmation({ message: `Remove ${prop}?`, pref: PREF_REMOVE_PROP })) {
        return;
      }

      this.inputSelected.onSubRemove(prop, this.type, this.settings);

      this.$delete(this.type.options.props, prop);
      this.$delete(this.settings.sub, prop);

      const transform = this.type.getValueChangeExpression(Exprs.noop(), prop, undefined);

      this.change({ transform });
      this.propChange++;

      this.$emit('prop:remove', prop);
    },
    async rename(prop: string) {
      const newProp = await getInput({ 
        title: 'Rename',
        message: 'Enter a new name for this property',
        label: 'Name',
        value: prop,
      });

      if (!newProp) {
        return;
      }
      if (newProp === prop) {
        return sendNotification({ message: 'The property name will remain the same.' });
      }
      if (newProp in this.type.options.props) {
        return sendNotification({ message: 'A property with that name already exists.' });
      }

      this.inputSelected.onSubMove(prop, newProp, this.type, this.settings);

      const propType = this.type.options.props[prop];
      const propSettings = this.settings.sub[prop];

      this.$delete(this.type.options.props, prop);
      this.$delete(this.settings.sub, prop);

      this.$set(this.type.options.props, newProp, propType);
      this.$set(this.settings.sub, newProp, propSettings);

      sendNotification({ message: `${prop} renamed to ${newProp}` });

      const transform = this.type.getValueChangeExpression(Exprs.noop(), prop, newProp);

      this.change({ transform });

      this.$emit('prop:rename', [prop, newProp]);
    },
    onChange(prop: string, event: TypeUpdateEvent) {
      this.type.options.props[prop] = event.type;
      this.settings.sub[prop] = event.settings;

      const transform = event.transform
        ? this.type.getValueChangeExpression(event.transform, prop, prop)
        : undefined;

      this.change({ transform });

      this.$emit('prop:change', prop);
    },
  },
});
